import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import deepEqual from 'deep-equal';
import { v4 as uuidv4 } from 'uuid';
import type { Content, ContentType } from '@ilteducation/content-registry-types';
import { Filter, GroupFilterMode, SearchQuery, Sort } from './types';
import { searchContent } from './api';
import { useClientAnalytics } from '@ilteducation/client-analytics';

type SearchState<C extends Content> = {
  searchOperationId: string; // Used for analytics
  contentType: string;
  query?: string;
  filter?: Filter;
  sort?: Sort[];
  cursor: string | null;
  pageSize?: number;
  pageTotal: number;
  isLoading: boolean;
  contents: C[];
  groupFilterMode: GroupFilterMode;
  randomize: boolean;
};

const getRandomSeed = (): number => {
  return Math.floor(Math.random() * 1000000);
};

const useSearch = <C extends Content>(contentType: ContentType) =>
  defineStore(`content-search-${contentType}`, () => {
    const clientAnalytics = useClientAnalytics();
    const { sendAnalyticsEvent } = clientAnalytics;

    const searchState = ref<SearchState<C>>();

    /**
     * We generate the random seed only once per store instance.
     * This is to ensure that the randomization is consistent across searches - for pagination purposes.
     */
    const randomSeed = ref<number>(getRandomSeed());

    const hasError = ref(false);

    const result = computed<C[]>(() => searchState.value?.contents ?? []);
    const isLoading = computed(() => searchState.value?.isLoading ?? false);
    const hasMore = computed(() => searchState.value?.cursor !== null);
    const pageTotal = computed(() => searchState.value?.pageTotal ?? 0);
    const search = (
      query: string | null,
      filter: Filter,
      limit: number,
      sort: Sort[] = [],
      groupFilterMode: GroupFilterMode = 'or',
      randomize: boolean = false,
      source?: string,
    ) => {
      // No early return while a request is in flight. A new search supersedes the old one: its
      // response is dropped by the staleness check below, and the identical-query check further
      // down still stops the same search being fired twice. Skipping here instead meant switching
      // category during a page load silently dropped the new category.

      // Vuetify's clearable VTextField sets the bound model to null when cleared,
      // so `query` can arrive as null even though callers type it as a string.
      const formattedQueryString = (query ?? '').trim();

      if (
        formattedQueryString === searchState.value?.query &&
        deepEqual(filter, searchState.value?.filter) &&
        deepEqual(sort, searchState.value?.sort)
      ) {
        console.debug('Search query is considered the same, skipping.');
        return;
      }

      /* Parameter "filter" is passed in as a reference,
    which causes the deepEqual to always be truthy, so solve this,
    we create a deep copy of it */
      const formatedFilter: Filter = JSON.parse(JSON.stringify(filter));

      hasError.value = false;
      searchState.value = {
        searchOperationId: uuidv4(),
        contentType,
        query: formattedQueryString,
        filter: formatedFilter,
        sort,
        cursor: null,
        pageSize: limit,
        pageTotal: 0,
        isLoading: true,
        contents: [],
        groupFilterMode,
        randomize,
      };

      const searchQuery: SearchQuery = {
        q: formattedQueryString,
        filter,
        pagination: { size: limit },
        sort,
        groupFilterMode,
      };

      if (randomize) {
        /**
         * Even if we send a random seed every time, the search API will NOT randomize the results
         * if the query is provided or if sort parameters are specified.
         */
        searchQuery.randomSeed = randomSeed.value;
      }

      const operationId = searchState.value.searchOperationId;
      // A response only belongs to the search that is still current. Anything older is a category
      // the user has already left, and merging it in is how results from two categories ended up
      // in the same grid.
      const isStale = () => searchState.value?.searchOperationId !== operationId;

      searchContent<C>(contentType, searchQuery)
        .then(({ data, cursor, total }) => {
          if (isStale()) return;
          searchState.value = {
            ...searchState.value!,
            cursor,
            pageTotal: total,
            isLoading: false,
            contents: data,
          };

          sendAnalyticsEvent('SearchEventType', {
            searchOperationId: searchState.value.searchOperationId,
            contentType: searchState.value.contentType,
            originalSearchTerm: formattedQueryString,
            filters: formatedFilter,
            sortOptions: sort,
            searchResultsCount: total,
            source,
          });
        })
        .catch(() => {
          if (isStale()) return;
          searchState.value = undefined;
          hasError.value = true;
        });
    };

    const next = () => {
      if (!searchState.value || isLoading.value || !hasMore.value) {
        return;
      }

      hasError.value = false;
      searchState.value = {
        ...searchState.value,
        isLoading: true,
      };

      const searchQuery: SearchQuery = {
        q: searchState.value.query,
        filter: searchState.value.filter,
        pagination: { size: searchState.value.pageSize!, cursor: searchState.value.cursor! },
        sort: searchState.value?.sort,
        groupFilterMode: searchState.value?.groupFilterMode,
      };

      if (searchState.value.randomize) {
        searchQuery.randomSeed = randomSeed.value;
      }
      const operationId = searchState.value.searchOperationId;

      // The page belongs to the search that requested it — if a new search has started since,
      // appending it would paste the previous category's materials onto the new results.
      const isStale = () => searchState.value?.searchOperationId !== operationId;

      searchContent<C>(searchState.value.contentType, searchQuery)
        .then(({ data, cursor }) => {
          if (isStale()) return;
          searchState.value = {
            ...searchState.value!,
            cursor,
            isLoading: false,
            contents: [...searchState.value!.contents, ...data],
          };
        })
        .catch(() => {
          if (isStale()) return;
          searchState.value = { ...searchState.value!, isLoading: false };
          hasError.value = true;
        });
    };

    const clear = () => {
      searchState.value = undefined;
      hasError.value = false;
    };

    /**
     * We send the position as a parameter, because sometimes the results are grouped or the display order is changed.
     *
     * @param contentId
     * @param positionInSearchResults
     */
    const trackResultClick = (contentId: string, positionInSearchResults: number) => {
      if (!searchState.value) return;

      sendAnalyticsEvent('SearchResultClickEventType', {
        searchOperationId: searchState.value.searchOperationId,
        contentType: searchState.value.contentType,
        searchResultId: contentId,
        searchResultPosition: positionInSearchResults,
      });
    };

    return {
      result,
      isLoading,
      hasMore,
      hasError,
      total: pageTotal,
      search,
      next,
      clear,
      trackResultClick,
    };
  })();

export default useSearch;
